import {
	fieldMode,
	fieldType,
	fieldsGroupType,
	createFormProps,
	formThemes,
	dependencyAction,
	fieldDependencyType,
	customValueTypes,
	validationTypes,
} from 'components/FormCreatorComponent/types';
import {
	beneficiaryStatusOptions,
	customerAccountDetailsTypes,
	withdrawalBalanceSteps,
	bankAccountStatusOptions,
} from 'constants/customer-account.constants';
import _ from 'lodash';
import {
	getAltshulerAccountsOptions,
	getCompanyBankAccountsOptions,
	getCompanyContactsOptions,
	getCompanyWithdrawalCurrenciesOptions,
} from './helper';
import {
	ContactLegalStatus,
	accountStatus,
	contactStatusOptions,
	formatDate,
	genderOptions,
	permissionOptions,
	riskTypeOptions,
} from 'constants/global.constants';
import { timing } from 'constants/ui.constants';
import { isValid, format } from 'date-fns';
import { getFile } from 'services/global.service';
import { BeneficiaryPaymentScheme } from './BottomSection/BeneficiaryPaymentScheme';
import { BeneficiaryBankDetails } from './BottomSection/BeneficiaryBankDetails';
import { getDeclinedReasonText } from './BottomSection/tablesConfig';
import { ButtonComponent } from 'components/ButtonComponent';
import { formatNumber } from 'components/FormCreatorComponent/helpers';
import { PaymentStatus, PaymentStatusHebrew } from 'constants/payment.constants';

const createFileField = (index: number, label: string, required: boolean = false) => ({
	type: fieldType.file,
	mode: fieldMode.readonly,
	props: {
		name: `files[${index}]`,
		label,
		required,
		disabled: true,
		onClick: (file: { key?: string; companyId?: number }) => {
			if (file?.key && file?.companyId) {
				getFile(file.key, file.companyId);
			}
		},
	},
	validation: [{ requierd: required }],
	dependencyAction: dependencyAction.hide,
	dependency: {
		name: `files[${index}]`,
		value: '',
		type: fieldDependencyType.isCustomDependency,
		custom: (record: Record<string, any>) => {
			return record[`files[${index}]`] === '';
		},
	},
});

const createMultipleFileFields = (count: number, firstFieldRequired: boolean = false, firstFieldLabel?: string) => {
	return Array.from({ length: count }, (_, index) => {
		const label =
			index === 0 && firstFieldLabel
				? firstFieldLabel
				: index === 0
					? 'page.pendingRequests.field.fileReference'
					: `page.pendingRequests.field.fileReference${index + 1}`;
		const required = index === 0 ? firstFieldRequired : false;
		return createFileField(index, label, required);
	});
};

const initialBeneficiaryForms = [
	{
		name: 'page.customerAccounts.forms.beneficiaries.beneficiaryDetails.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'typeHebrew',
							label: 'page.customerAccounts.forms.beneficiaries.beneficiaryDetails.beneficiaryType',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'companyName',
							label: 'page.customerAccounts.forms.beneficiaries.beneficiaryDetails.companyName',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'nickname',
							label: 'page.customerAccounts.forms.beneficiaries.beneficiaryDetails.nickname',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'identificationNumber',
							label: 'page.customerAccounts.forms.beneficiaries.beneficiaryDetails.identificationNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'currency.name',
							label: 'page.customerAccounts.forms.beneficiaries.beneficiaryDetails.currencyName',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'address',
							label: 'page.customerAccounts.forms.beneficiaries.beneficiaryDetails.address',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'city',
							label: 'page.customerAccounts.forms.beneficiaries.beneficiaryDetails.city',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'country.name',
							label: 'page.customerAccounts.forms.beneficiaries.beneficiaryDetails.country',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'website',
							label: 'page.customerAccounts.forms.beneficiaries.beneficiaryDetails.website',
							required: true,
							disabled: true,
						},
						dependency: { name: 'website', value: null, type: fieldDependencyType.isEmpty },
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'note',
							label: 'page.customerAccounts.forms.beneficiaries.beneficiaryDetails.note',
							required: true,
							disabled: true,
						},
						dependency: { name: 'note', value: null, type: fieldDependencyType.isEmpty },
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'comment',
							label: 'page.pendingRequests.field.comment',
							required: true,
							disabled: true,
						},
						dependency: { name: 'comment', value: null, type: fieldDependencyType.isEmpty },
					},
					{
						type: fieldType.file,
						mode: fieldMode.readonly,
						props: {
							name: 'formalBankApproveFile',
							label: 'page.pendingRequests.field.formalBankApproveFile',
							disabled: true,
							onClick: (formalBankApproveFile: any) => {
								getFile(formalBankApproveFile?.key, formalBankApproveFile?.companyId);
							},
						},
						dependency: { name: 'formalBankApproveFile', value: null, type: fieldDependencyType.isEmpty },
					},
					...createMultipleFileFields(6, false),
				],
			},
		],
	},
	{
		name: 'page.customerAccounts.forms.beneficiaries.bankDetails.title',
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.row,
				fields: [
					{
						type: fieldType.custom,
						mode: fieldMode.readonly,
						props: {
							name: 'bankDetailsInfo',
							label: 'page.customerAccounts.forms.beneficiaries.bankDetails.details',
							required: true,
							disabled: true,
							customComponent: BeneficiaryBankDetails,
						},
					},
				],
			},
			{
				type: fieldsGroupType.row,
				fields: [
					{
						type: fieldType.custom,
						mode: fieldMode.readonly,
						props: {
							name: 'paymentInfo',
							label: 'page.customerAccounts.forms.beneficiaries.bankDetails.swift',
							required: true,
							disabled: true,
							customComponent: BeneficiaryPaymentScheme,
						},
					},
				],
			},
		],
	},
];

/** Separate FormCard after payment categories on the beneficiary detail page. */
export const beneficiaryHyperSyncForm = {
	name: 'page.customerAccounts.forms.beneficiaries.hyperSync.title',
	theme: formThemes.hiddenInputs,
	handleChange: () => { },
	fieldsGroups: [
		{
			type: fieldsGroupType.col,
			fields: [
				{
					type: fieldType.text,
					mode: fieldMode.readonly,
					props: {
						name: 'hyperSyncStatus',
						label: 'page.customerAccounts.tables.beneficiaries.field.statusHebrew',
						required: false,
						disabled: true,
					},
				},
				{
					type: fieldType.text,
					mode: fieldMode.readonly,
					props: {
						name: 'hyperSyncLastAction',
						label: 'page.customerAccounts.forms.beneficiaries.hyperSync.lastAction',
						required: false,
						disabled: true,
					},
				},
				{
					type: fieldType.text,
					mode: fieldMode.readonly,
					props: {
						name: 'hyperSyncLastAttemptAt',
						label: 'page.customerAccounts.forms.beneficiaries.hyperSync.lastAttemptAt',
						required: false,
						disabled: true,
					},
				},
			],
		},
	],
};

const initialContactForms = [
	{
		name: 'page.customerAccounts.forms.contacts.basicDetails.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'firstNameHebrew',
							label: 'page.customerAccounts.forms.contacts.basicDetails.firstNamHebrew',
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }, { [validationTypes.hebrewLettersOnly]: true }],
					},
					{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'lastNameHebrew',
							label: 'page.customerAccounts.forms.contacts.basicDetails.lastNameHebrew',
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }, { [validationTypes.hebrewLettersOnly]: true }],
					},
					{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'firstName',
							label: 'page.customerAccounts.forms.contacts.basicDetails.firstNameEnglish',
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }, { [validationTypes.latinLettersOnly]: true }],
					},
					{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'lastName',
							label: 'page.customerAccounts.forms.contacts.basicDetails.lastNameEnglish',
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }, { [validationTypes.latinLettersOnly]: true }],
					},
					{
						type: fieldType.datePicker,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'dateOfBirth',
							label: 'page.customerAccounts.forms.contacts.basicDetails.dateOfBirth',
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }],
					},
					{
						type: fieldType.select,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'legalStatus',
							label: 'label.publicFigure',
							options: [
								{ value: ContactLegalStatus.Legal, label: 'label.yes', disabled: false },
								{ value: ContactLegalStatus.NotLegal, label: 'label.no', disabled: false },
							],
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }],
						customValue: (value) => (value === ContactLegalStatus.Legal ? 'כן' : 'לא'),
					},

					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'identificationTypeHebrew',
							label: 'page.customerAccounts.forms.contacts.basicDetails.identificationType',
							required: false,
							disabled: true,
						},
						validation: [{ requierd: false }],
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'identificationNumber',
							label: 'page.customerAccounts.forms.contacts.basicDetails.identificationNumber',
							required: false,
							disabled: true,
						},
						validation: [{ requierd: true }, { [validationTypes.digitsOnly]: true }],
					},
					/*
					{
						type: fieldType.select,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'genderHebrew',
							label: 'page.customerAccounts.forms.contacts.basicDetails.gender',
							required: true,
							disabled: false,
							options: genderOptions,
						},
						validation: [{ requierd: true }],
					},
					*/
				],
			},
		],
	},
	{
		name: 'page.customerAccounts.forms.contacts.contactDetails.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'email',
							label: 'page.customerAccounts.forms.contacts.contactDetails.email',
							required: false,
							disabled: false,
						},
						validation: [{ requierd: true }, { email: true }],
					},
					{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'phone',
							label: 'page.customerAccounts.forms.contacts.contactDetails.phone',
							required: false,
							disabled: false,
						},
						validation: [{ requierd: true }, { phone: true }],
					},
					/*
					{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'address',
							label: 'page.customerAccounts.forms.contacts.contactDetails.address',
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }, { [validationTypes.hebrewLettersSpecialChars]: true }],
					},
					{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'city',
							label: 'page.customerAccounts.forms.contacts.contactDetails.city',
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }, { [validationTypes.hebrewLettersOnly]: true }],
					},
					{
						type: fieldType.select,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'country',
							label: 'page.customerAccounts.forms.contacts.contactDetails.country',
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }],
						customValue: (value: any) => value?.name,
					},
					*/
				],
			},
		],
	},
];

const initialBankAccountsForms = [
	{
		name: 'page.customerAccounts.forms.bankAccounts.bankDetails.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'accountOwnerName',
							label: 'page.customerAccounts.forms.bankAccounts.bankDetails.accountOwnerName',
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }],
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankName',
							label: 'page.customerAccounts.forms.bankAccounts.bankDetails.bankName',
							required: false,
							disabled: true,
						},
						validation: [{ requierd: false }],
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankCode',
							label: 'page.customerAccounts.forms.bankAccounts.bankDetails.bankCode',
							required: false,
							disabled: true,
						},
						validation: [{ requierd: false }],
					},
					{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'bankBranch',
							label: 'page.customerAccounts.forms.bankAccounts.bankDetails.bankBranch',
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }],
					},
					{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'accountNumber',
							label: 'page.customerAccounts.forms.bankAccounts.bankDetails.accountNumber',
							required: true,
							disabled: false,
						},
						validation: [{ requierd: true }],
					},
				],
			},
		],
	},
];

const initialBalanceAccountForms = [
	{
		name: 'page.customerAccounts.forms.balance.transferringAccount.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankAccount.accountOwnerName',
							label: 'page.customerAccounts.forms.balance.transferringAccount.accountOwnerName',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankAccount.bankName.bankName',
							label: 'page.customerAccounts.forms.balance.transferringAccount.bankName',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankAccount.bankName.bankCode',
							label: 'page.customerAccounts.forms.balance.transferringAccount.bankNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankAccount.bankBranch',
							label: 'page.customerAccounts.forms.balance.transferringAccount.branchNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankAccount.accountNumber',
							label: 'page.customerAccounts.forms.balance.transferringAccount.accountNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'currency.name',
							label: 'page.customerAccounts.forms.balance.transferringAccount.currency',
							required: true,
							disabled: true,
						},
					},
				],
			},
		],
	},
	{
		name: 'page.customerAccounts.forms.balance.receivingAccount.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'altshulerAccount.bank.bankName',
							label: 'page.customerAccounts.forms.balance.receivingAccount.bankName',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'altshulerAccount.bank.bankCode',
							label: 'page.customerAccounts.forms.balance.receivingAccount.bankNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'altshulerAccount.bankBranch',
							label: 'page.customerAccounts.forms.balance.receivingAccount.branchNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'altshulerAccount.accountNumber',
							label: 'page.customerAccounts.forms.balance.receivingAccount.accountNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'altshulerAccount.currency.name',
							label: 'page.customerAccounts.forms.balance.receivingAccount.currency',
							required: true,
							disabled: true,
						},
					},
				],
			},
		],
	},
	{
		name: 'page.customerAccounts.forms.balance.details.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'createdAt',
							label: 'page.customerAccounts.forms.balance.details.createdAt',
							required: true,
							disabled: true,
						},
						customValue: (value) => formatDate(value),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'completedAt',
							label: 'page.customerAccounts.forms.balance.details.makeDate',
							required: true,
							disabled: true,
						},
						customValue: (value) => formatDate(value),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'settlementDate',
							label: 'page.customerAccounts.forms.balance.details.endDate',
							required: true,
							disabled: true,
						},
						customValue: (value) => formatDate(value),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'reference',
							label: 'page.customerAccounts.forms.balance.details.reference',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'amount',
							label: 'page.customerAccounts.forms.balance.details.amount',
							required: true,
							disabled: true,
						},
						customValueType: customValueTypes.integer,
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'currency.name',
							label: 'page.customerAccounts.forms.balance.details.currency',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'createdBy',
							label: 'page.customerAccounts.forms.balance.details.createdBy',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'approverName',
							label: 'page.customerAccounts.forms.balance.details.approvedBy',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'ccHouseAccountTransferReference',
							label: 'page.customerAccounts.forms.balance.details.withHouseAccount',
							required: true,
							disabled: true,
						},
						customValue: (value) => (value ? 'כן' : 'לא'),
					},
				],
			},
		],
	},
	{
		name: 'page.customerAccounts.forms.balance.accountManagement.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'ccId',
							label: 'page.customerAccounts.forms.balance.accountManagement.ccId',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'mainSigner',
							label: 'page.customerAccounts.forms.balance.accountManagement.boApprovedByMainSignerUser',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'boMainSignerApprovalDate',
							label: 'page.customerAccounts.forms.balance.accountManagement.firstSignerAnswer',
							required: true,
							disabled: true,
						},
						customValue: (value) => (value ? 'אישר' : 'לא קיים'),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'secondarySigner',
							label: 'page.customerAccounts.forms.balance.accountManagement.boApprovedBySecondarySignerUser',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'boSecondarySignerApprovalDate',
							label: 'page.customerAccounts.forms.balance.accountManagement.secondSignerAnswer',
							required: true,
							disabled: true,
						},
						customValue: (value) => (value ? 'אישר' : 'לא קיים'),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'updatedBy',
							label: 'page.customerAccounts.forms.balance.accountManagement.updatedBy',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'updatedAt',
							label: 'page.customerAccounts.forms.balance.accountManagement.updatedAt',
							required: true,
							disabled: true,
						},
						customValue: (value) => formatDate(value),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'status',
							label: 'page.customerAccounts.forms.balance.accountManagement.status',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.textArea,
						mode: fieldMode.readonly,
						props: {
							name: 'boNotes',
							label: 'page.customerAccounts.forms.balance.accountManagement.rejectReason',
							required: true,
							disabled: true,
						},
					},
				],
			},
		],
	},
	{
		name: 'page.customerAccounts.forms.balance.file.title',
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.file,
						mode: fieldMode.readonly,
						props: {
							name: 'file',
							label: 'page.customerAccounts.forms.balance.file.file',
							required: true,
							disabled: true,
							onClick: (file) => {
								getFile(file?.key, file?.companyId);
							},
						},
					},
				],
			},
		],
	},
];

const initialWithdrawalBalanceAccountForms = [
	{
		name: 'page.customerAccounts.forms.balance.transferringAccount.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'altshulerAccount.bank.bankName',
							label: 'page.customerAccounts.forms.balance.receivingAccount.bankName',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'altshulerAccount.bank.bankCode',
							label: 'page.customerAccounts.forms.balance.receivingAccount.bankNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'altshulerAccount.bankBranch',
							label: 'page.customerAccounts.forms.balance.receivingAccount.branchNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'altshulerAccount.accountNumber',
							label: 'page.customerAccounts.forms.balance.receivingAccount.accountNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'altshulerAccount.currency.name',
							label: 'page.customerAccounts.forms.balance.receivingAccount.currency',
							required: true,
							disabled: true,
						},
					},
				],
			},
		],
	},
	{
		name: 'page.customerAccounts.forms.balance.receivingAccount.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankAccount.accountOwnerName',
							label: 'page.customerAccounts.forms.balance.transferringAccount.accountOwnerName',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankAccount.bankName.bankName',
							label: 'page.customerAccounts.forms.balance.transferringAccount.bankName',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankAccount.bankName.bankCode',
							label: 'page.customerAccounts.forms.balance.transferringAccount.bankNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankAccount.bankBranch',
							label: 'page.customerAccounts.forms.balance.transferringAccount.branchNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'bankAccount.accountNumber',
							label: 'page.customerAccounts.forms.balance.transferringAccount.accountNumber',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'currency.name',
							label: 'page.customerAccounts.forms.balance.transferringAccount.currency',
							required: true,
							disabled: true,
						},
					},
				],
			},
		],
	},
	{
		name: 'page.customerAccounts.forms.balance.details.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'createdAt',
							label: 'page.customerAccounts.forms.balance.details.createdAt',
							required: true,
							disabled: true,
						},
						customValue: (value) => formatDate(value),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'completedAt',
							label: 'page.customerAccounts.forms.balance.details.makeDate',
							required: true,
							disabled: true,
						},
						customValue: (value) => formatDate(value),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'settlementDate',
							label: 'page.customerAccounts.forms.balance.details.endDate',
							required: true,
							disabled: true,
						},
						customValue: (value) => formatDate(value),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'reference',
							label: 'page.customerAccounts.forms.balance.details.reference',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'amount',
							label: 'page.customerAccounts.forms.balance.details.amount',
							required: true,
							disabled: true,
						},
						customValueType: customValueTypes.integer,
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'currency.name',
							label: 'page.customerAccounts.forms.balance.details.currency',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'createdBy',
							label: 'page.customerAccounts.forms.balance.details.createdBy',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'approverName',
							label: 'page.customerAccounts.forms.balance.details.approvedBy',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'ccHouseAccountTransferReference',
							label: 'page.customerAccounts.forms.balance.details.withHouseAccount',
							required: true,
							disabled: true,
						},
						customValue: (value) => (value ? 'כן' : 'לא'),
					},
				],
			},
		],
	},
	{
		name: 'page.customerAccounts.forms.balance.accountManagement.title',
		theme: formThemes.hiddenInputs,
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'ccId',
							label: 'page.customerAccounts.forms.balance.accountManagement.ccId',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'mainSigner',
							label: 'page.customerAccounts.forms.balance.accountManagement.boApprovedByMainSignerUser',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'boMainSignerApprovalDate',
							label: 'page.customerAccounts.forms.balance.accountManagement.firstSignerAnswer',
							required: true,
							disabled: true,
						},
						customValue: (value) => (value ? 'אישר' : 'לא קיים'),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'secondarySigner',
							label: 'page.customerAccounts.forms.balance.accountManagement.boApprovedBySecondarySignerUser',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'boSecondarySignerApprovalDate',
							label: 'page.customerAccounts.forms.balance.accountManagement.secondSignerAnswer',
							required: true,
							disabled: true,
						},
						customValue: (value) => (value ? 'אישר' : 'לא קיים'),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'updatedBy',
							label: 'page.customerAccounts.forms.balance.accountManagement.updatedBy',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'updatedAt',
							label: 'page.customerAccounts.forms.balance.accountManagement.updatedAt',
							required: true,
							disabled: true,
						},
						customValue: (value) => formatDate(value),
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'status',
							label: 'page.customerAccounts.forms.balance.accountManagement.status',
							required: true,
							disabled: true,
						},
					},
					{
						type: fieldType.textArea,
						mode: fieldMode.readonly,
						props: {
							name: 'boNotes',
							label: 'page.customerAccounts.forms.balance.accountManagement.rejectReason',
							required: true,
							disabled: true,
						},
					},
				],
			},
		],
	},
	{
		name: 'page.customerAccounts.forms.balance.file.title',
		handleChange: () => { },
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.file,
						mode: fieldMode.readonly,
						props: {
							name: 'file',
							label: 'page.customerAccounts.forms.balance.file.file',
							required: true,
							disabled: true,
							onClick: (file) => {
								getFile(file?.key, file?.companyId);
							},
						},
					},
				],
			},
		],
	},
];

export interface formConfig {
	[key: string]: createFormProps[];
}

export const customerDashboardTypesForms: formConfig = {
	[customerAccountDetailsTypes.PLAN]: [
		{
			name: 'page.customerAccounts.forms.plan.details.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'plan.name',
								label: 'page.customerAccounts.forms.plan.details.name',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						// {
						// 	type: fieldType.text,
						// 	mode: fieldMode.readonly,
						// 	props: {
						// 		name: 'plan.type',
						// 		label: 'page.customerAccounts.forms.plan.details.planType',
						// 		required: true,
						// 		disabled: true,
						// 	},
						// 	validation: [{ requierd: false }],
						// },
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdAt',
								label: 'page.customerAccounts.forms.plan.details.joinDate',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) => formatDate(value),
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'plan.monthlyFeeAmount',
								label: 'page.customerAccounts.forms.plan.details.costs',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }, { integer: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'changedByContact',
								label: 'page.customerAccounts.forms.plan.details.updatedBy',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }, { integer: true }],
							customValue: (value) => (value ? `${value?.firstName} ${value?.lastName}` : ''),
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.nextMonthPlan.details.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'nextMonthPlan.plan.name',
								label: 'page.customerAccounts.forms.plan.details.name',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						// {
						// 	type: fieldType.text,
						// 	mode: fieldMode.readonly,
						// 	props: {
						// 		name: 'plan.type',
						// 		label: 'page.customerAccounts.forms.plan.details.planType',
						// 		required: true,
						// 		disabled: true,
						// 	},
						// 	validation: [{ requierd: false }],
						// },
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'nextMonthPlan.startDate',
								label: 'page.customerAccounts.forms.plan.details.joinDate',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) => formatDate(value),
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'nextMonthPlan.plan.monthlyFeeAmount',
								label: 'page.customerAccounts.forms.plan.details.costs',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }, { integer: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'nextMonthPlan.changedByContact',
								label: 'page.customerAccounts.forms.plan.details.updatedBy',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }, { integer: true }],
							customValue: (value) => (value ? `${value?.firstName} ${value?.lastName}` : ''),
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.PAYMENT]: [
		{
			name: 'page.customerAccounts.forms.payment.details.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdAt',
								label: 'page.customerAccounts.forms.payment.details.createdAt',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'signerApprovalDate',
								label: 'page.customerAccounts.forms.payment.details.signedAt',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'boApprovalDate',
								label: 'page.customerAccounts.forms.payment.details.boApprovalDate',
								required: true,
								disabled: true,
							},
							dependency: { name: 'boApprovalDate', value: null, type: fieldDependencyType.equals },
							validation: [{ requierd: false }],
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'settlementDate',
								label: 'page.customerAccounts.forms.payment.details.settlementDate',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependency: { name: 'settlementDate', value: null, type: fieldDependencyType.isEmpty },
							customValue: (value) =>
								value && isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'dueDate',
								label: 'page.customerAccounts.forms.payment.details.dueDate',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							validation: [{ requierd: false }],
							dependency: { name: 'dueDate', value: null, type: fieldDependencyType.isEmpty },
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.DATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'dueDatePrev',
								label: 'page.customerAccounts.forms.payment.details.dueDatePrev',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							validation: [{ requierd: false }],
							dependency: { name: 'dueDatePrev', value: null, type: fieldDependencyType.isEmpty },
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.DATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'reference',
								label: 'page.customerAccounts.forms.payment.details.reference',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'amount',
								label: 'page.customerAccounts.forms.payment.details.amount',
								required: true,
								disabled: true,
							},
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'currency.name',
								label: 'page.customerAccounts.forms.payment.details.currency',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'typeOfPaymentHebrew',
								label: 'page.customerAccounts.forms.payment.details.typeOfPayment',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'swiftFeeTypeHebrew',
								label: 'page.customerAccounts.forms.payment.details.feeType',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bicSwift',
								label: 'page.customerAccounts.forms.payment.details.swiftNumber',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'fee.amount',
								label: 'page.customerAccounts.forms.payment.details.fee',
								required: true,
								disabled: true,
							},
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.select,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'fee.statusHebrew',
								label: 'page.customerAccounts.forms.payment.status.fee_status',
								required: true,
								disabled: false,
								options: Object.keys(PaymentStatusHebrew).map((key: string) => {
									return {
										value: key,
										label: PaymentStatusHebrew[key as keyof typeof PaymentStatusHebrew],
									};
								}),
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'paymentCategoryDisplay',
								label: 'page.customerAccounts.forms.payment.category.title',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'purposeCode',
								label: 'page.customerAccounts.forms.payment.details.purposeCode',
								required: false,
								disabled: true,
							},
						},
						{
							type: fieldType.textArea,
							mode: fieldMode.readonly,
							props: {
								name: 'paymentComment',
								label: 'page.customerAccounts.forms.payment.details.paymentComment',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'complianceReference',
								label: 'page.customerAccounts.forms.payment.details.complianceReference',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'invoiceNumber',
								label: 'page.customerAccounts.forms.payment.details.invoiceNumber',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdBy',
								label: 'page.customerAccounts.forms.payment.details.createdBy',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'signedBy',
								label: 'page.customerAccounts.forms.payment.details.signedBy',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'approverName',
								label: 'page.customerAccounts.forms.payment.details.approvedBy',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.payment.beneficiary.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.companyName',
								label: 'page.customerAccounts.forms.payment.beneficiary.companyName',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.nickname',
								label: 'page.customerAccounts.forms.payment.beneficiary.nickname',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.country.name',
								label: 'page.customerAccounts.forms.payment.beneficiary.country',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bankAccountHolderName',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.bankAccountHolderName',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bankCode',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.bankCode',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.branchCode',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.branchCode',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bankAccountAddress',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.bankAccountAddress',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bankAccountCity',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.bankAccountCity',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bankAccountCountry.name',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.bankAccountCountry',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},

						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.currency.name',
								label: 'page.customerAccounts.forms.payment.beneficiary.currency',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiaryStatusHebrew',
								label: 'page.customerAccounts.forms.payment.beneficiary.status',
								required: false,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.payment.reference.title',
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						...createMultipleFileFields(5, true, 'page.customerAccounts.forms.payment.reference.file'),
					],
				},
			],
		},
		// {
		// 	name: 'page.customerAccounts.forms.payment.accountManagementDetails.title',
		// 	theme: formThemes.hiddenInputs,
		// 	handleChange: () => {},
		// 	fieldsGroups: [
		// 		{
		// 			type: fieldsGroupType.col,
		// 			fields: [
		// 				{
		// 					type: fieldType.text,
		// 					mode: fieldMode.readonly,
		// 					props: {
		// 						name: 'ccId',
		// 						label: 'page.customerAccounts.forms.payment.accountManagementDetails.ccId',
		// 						required: true,
		// 						disabled: true,
		// 					},
		// 					validation: [{ requierd: false }],
		// 				},
		// 				{
		// 					type: fieldType.text,
		// 					mode: fieldMode.readonly,
		// 					props: {
		// 						name: 'boApprovedByUser.firstName',
		// 						label: 'page.customerAccounts.forms.payment.accountManagementDetails.boApprovedByUser',
		// 						required: true,
		// 						disabled: true,
		// 					},
		// 					validation: [{ requierd: false }],
		// 				},
		// 				{
		// 					type: fieldType.text,
		// 					mode: fieldMode.readonly,
		// 					props: {
		// 						name: 'updatedAt',
		// 						label: 'page.customerAccounts.forms.payment.accountManagementDetails.updatedAt',
		// 						required: true,
		// 						disabled: true,
		// 					},
		// 					validation: [{ requierd: false }],
		// 					customValue: (value) =>
		// 						isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
		// 				},
		// 				{
		// 					type: fieldType.text,
		// 					mode: fieldMode.readonly,
		// 					props: {
		// 						name: 'statusHebrew',
		// 						label: 'page.customerAccounts.forms.payment.status.status',
		// 						required: true,
		// 						disabled: true,
		// 					},
		// 					validation: [{ requierd: false }],
		// 				},
		// 				{
		// 					type: fieldType.textArea,
		// 					mode: fieldMode.readonly,
		// 					props: {
		// 						name: 'rejectReason',
		// 						label: 'page.customerAccounts.forms.payment.status.rejectReason',
		// 						required: true,
		// 						disabled: true,
		// 					},
		// 					validation: [{ requierd: false }],
		// 				},

		// 			],
		// 		},
		// 	],
		// },
		{
			name: 'page.customerAccounts.forms.payment.accountManagementDetails.status.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'ccId',
								label: 'page.customerAccounts.forms.payment.accountManagementDetails.ccId',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependencyAction: dependencyAction.hide,
							// dependency: { name: 'ccId', value: null, type: fieldDependencyType.equals },
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'boApprovedByUser.firstName',
								label: 'page.customerAccounts.forms.payment.accountManagementDetails.boApprovedByUser',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependencyAction: dependencyAction.hide,
							dependency: {
								name: 'boApprovedByUser.firstName',
								value: null,
								type: fieldDependencyType.equals,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedAt',
								label: 'page.customerAccounts.forms.payment.accountManagementDetails.updatedAt',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.select,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'statusHebrew',
								label: 'page.customerAccounts.forms.payment.status.status',
								required: true,
								disabled: false,
								options: Object.keys(PaymentStatusHebrew).map((key: string) => {
									return {
										value: key,
										label: PaymentStatusHebrew[key as keyof typeof PaymentStatusHebrew],
									};
								}),
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.textArea,
							mode: fieldMode.readonly,
							props: {
								name: 'rejectReason',
								label: 'page.customerAccounts.forms.payment.status.rejectReason',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'rejectReason', value: null, type: fieldDependencyType.equals },
						},
						{
							type: fieldType.textArea,
							mode: fieldMode.readonly,
							props: {
								name: 'boNotes',
								label: 'page.customerAccounts.forms.payment.status.rejectReason',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'boNotes', value: null, type: fieldDependencyType.isEmpty },
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.balance_refund.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.ccId',
								label: 'page.customerAccounts.forms.balance_refund.cc_id',
								required: true,
								disabled: true,
								options: [],
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.currencyId',
								label: 'page.customerAccounts.forms.balance_refund.currency',
								disabled: true,
							},
							customValue: (value) => value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.amount',
								label: 'page.customerAccounts.forms.balance_refund.amount',
								disabled: true,
							},
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.reason',
								label: 'page.customerAccounts.forms.balance_refund.reason',
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.note',
								label: 'page.customerAccounts.forms.balance_refund.note',
								disabled: true,
							},
						},

						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.reference',
								label: 'page.customerAccounts.forms.balance_refund.reference',
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.status',
								label: 'page.customerAccounts.forms.balance_refund.status',
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.createdByBoUser',
								label: 'page.customerAccounts.forms.balance_refund.created_by_bo_user',
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.createdAt',
								label: 'page.customerAccounts.forms.balance_refund.created_at',
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.settlementDate',
								label: 'page.customerAccounts.forms.balance_refund.settlement_date',
								disabled: true,
							},
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.balance_refund.create_refund_title',
			checkInitValidation: false,
			theme: formThemes.regular,
			cardStyle: {
				width: '100% !important',
			},
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.row,
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.create.currencyId',
								label: 'page.customerAccounts.forms.balance_refund.currency',
								required: true,
								disabled: true,
								options: [],
							},
							customValue: (value) => value,

							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.create.amount',
								label: 'page.customerAccounts.forms.balance_refund.amount',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }, { [validationTypes.integer]: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.create.note',
								label: 'page.customerAccounts.forms.balance_refund.reason',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.custom,
							mode: fieldMode.readonly,
							props: {
								name: 'refund.create.create_button',
								label: 'page.customerAccounts.forms.balance_refund.create_button',
								required: false,
								disabled: true,
								customComponent: ButtonComponent,
								sx: {
									minWidth: '154px',
									height: '48px',
									margin: '4px 0 0 48px',
								},
							},
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.BALANCE]: initialBalanceAccountForms,
	[customerAccountDetailsTypes.BALANCE_WITHDRAWAL]: initialWithdrawalBalanceAccountForms,
	[customerAccountDetailsTypes.CONVERSION]: [
		{
			name: 'page.customerAccounts.forms.conversion.details.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdAt',
								label: 'page.customerAccounts.forms.payment.details.createdAt',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) => formatDate(value),
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'pendingQuote.dueDate',
								label: 'page.customerAccounts.forms.payment.details.updatedAt',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) => formatDate(value),
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedAt',
								label: 'page.customerAccounts.forms.payment.details.dueDate',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) => formatDate(value),
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'reference',
								label: 'page.customerAccounts.forms.conversion.reference.reference',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'benefit.name',
								label: 'page.customerAccounts.forms.conversion.benefit.benefit',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'pendingQuote.altshulerBuyAmount',
								label: 'page.customerAccounts.forms.conversion.details.buyAmount',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'pendingQuote.clientBuyCurrency',
								label: 'page.customerAccounts.forms.conversion.details.buyCurrency',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'pendingQuote.altshulerSellAmount',
								label: 'page.customerAccounts.forms.conversion.details.sellAmount',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'pendingQuote.clientSellCurrency',
								label: 'page.customerAccounts.forms.conversion.details.sellCurrency',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'pendingQuote.finalRate',
								label: 'page.customerAccounts.forms.conversion.details.finalRate',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'pendingQuote.altshulerRate',
								label: 'page.customerAccounts.forms.conversion.details.altshulerRate',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdBy',
								label: 'page.customerAccounts.forms.conversion.createdBy',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'approverName',
								label: 'page.customerAccounts.forms.conversion.approverName',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.conversion.accountManagementDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'ccId',
								label: 'page.customerAccounts.forms.conversion.accountManagementDetails.ccId',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'status',
								label: 'page.customerAccounts.forms.conversion.accountManagementDetails.status',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.BASIC_DATA]: [
		{
			name: 'page.customerAccounts.forms.basicData.companyDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'hebrewName',
								label: 'page.customerAccounts.forms.basicData.companyDetails.hebrewName',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'englishName',
								label: 'page.customerAccounts.forms.basicData.companyDetails.englishName',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'customerType',
								label: 'page.customerAccounts.forms.basicData.companyDetails.customerType',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'companyIdentityNumber',
								label: 'page.customerAccounts.forms.basicData.companyDetails.companyIdentityNumber',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'addressHebrew',
								label: 'page.customerAccounts.forms.basicData.companyDetails.addressHebrew',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'address',
								label: 'page.customerAccounts.forms.basicData.companyDetails.address',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.basicData.accountDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'accountId',
								label: 'page.customerAccounts.forms.basicData.accountDetails.accountId',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'ccId',
								label: 'page.customerAccounts.forms.basicData.accountDetails.ccId',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'hyperAccountNumber',
								label: 'page.customerAccounts.forms.basicData.accountDetails.hyperAccountNumber',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'maxForPayment',
								label: 'page.customerAccounts.forms.basicData.accountDetails.maxForPayment',
								required: false,
								disabled: false,
							},
							suffix: '$',
							customValueType: customValueTypes.integer,
							validation: [{ integer: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'maxForConvert',
								label: 'page.customerAccounts.forms.basicData.accountDetails.maxForConvert',
								required: false,
								disabled: false,
							},
							suffix: '$',
							customValueType: customValueTypes.integer,
							validation: [{ integer: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'riskLevel',
								label: 'page.customerAccounts.forms.basicData.accountDetails.riskLevel',
								options: riskTypeOptions,
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'closedByBoUser',
								label: 'page.customerAccounts.forms.basicData.accountDetails.status',
								required: false,
								disabled: false,
							},
							customValue: (value) => (value === null ? accountStatus.ACTIVE : accountStatus.NOT_ACTIVE),
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.basicData.accountManagementDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'manager.name',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.manager',
								required: false,
								disabled: false,
							},
							customValue: (value) => value || 'לא משויך לעובד',
						},
						{
							type: fieldType.select,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'dealer.name',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.dealer',
								required: false,
								disabled: false,
							},
							customValue: (value) => value || 'לא משויך לעובד',
						},
						{
							type: fieldType.select,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'agent.name',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.agent',
								required: false,
								disabled: false,
							},
							customValue: (value) => value || 'לא משויך לעובד',
						},
						{
							type: fieldType.select,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'lead.name',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.lead',
								required: false,
								disabled: false,
							},
							customValue: (value) => value || 'לא משויך לעובד',
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedBy',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.updatedBy',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedAt',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.updatedAt',
								required: false,
								disabled: false,
							},
							customValue: (value) => formatDate(value),
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.BANK_ACCOUNTS]: [
		...initialBankAccountsForms,
		{
			name: 'page.customerAccounts.forms.bankAccounts.bankAccountManagementDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'statusHebrew',
								label: 'page.customerAccounts.forms.bankAccounts.bankAccountManagementDetails.status',
								required: true,
								disabled: false,
								options: bankAccountStatusOptions,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'boNotes',
								label: 'page.customerAccounts.forms.bankAccounts.bankAccountManagementDetails.rejectReason',
								required: false,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'lastUpdatedBy',
								label: 'page.customerAccounts.forms.bankAccounts.bankAccountManagementDetails.lastUpdatedBy',
								required: true,
								disabled: true,
							},
							//validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedAt',
								label: 'page.customerAccounts.forms.bankAccounts.bankAccountManagementDetails.updatedAt',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
							customValue: (value) => formatDate(value),
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.bankAccounts.file.title',
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.file,
							mode: fieldMode.readonly,
							props: {
								name: 'file',
								label: 'page.customerAccounts.forms.bankAccounts.file.reference',
								required: true,
								disabled: true,
								onClick: (file) => {
									getFile(file?.key, file?.companyId);
								},
							},
							validation: [{ requierd: true }],
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.CONTACTS]: [
		...initialContactForms,
		{
			name: 'page.customerAccounts.forms.contacts.accountManagementDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'role',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.role',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'permission',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.permission',
								options: permissionOptions,
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'maxForPayment',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.maxForPayment',
								required: true,
								disabled: false,
							},
							suffix: '$',
							validation: [{ requierd: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'maxForConvert',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.maxForConvert',
								required: true,
								disabled: false,
							},
							suffix: '$',
							validation: [{ requierd: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.select,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'statusHebrew',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.status',
								required: true,
								disabled: false,
								options: contactStatusOptions,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.textArea,
							mode: fieldMode.readonly,
							props: {
								name: 'boNotes',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.rejectReason',
								required: false,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'lastUpdatedByBoUser',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.updatedBy',
								required: false,
								disabled: true,
							},
							customValue: (value) => (value ? `${value?.firstName} ${value?.lastName}` : ''),
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedAt',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.updatedAt',
								required: false,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) => formatDate(value),
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.BENEFICIARIES]: [
		...initialBeneficiaryForms,
		{
			name: 'page.customerAccounts.forms.beneficiaries.accountDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'statusHebrew',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.status',
								options: beneficiaryStatusOptions,
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiaryDeniedMessages',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.rejectReason',
								required: false,
								disabled: true,
							},
							customValue: (value, record) => {
								return getDeclinedReasonText(record);
							},
							dependency: {
								name: 'beneficiaryDeniedMessages',
								value: null,
								type: fieldDependencyType.isCustomDependency,
								custom: (record) => {
									const lastRelevantRecord = _.findLast(
										record?.beneficiaryDeniedMessages,
										'createdByContactId'
									);
									return (
										_.isEmpty(record) ||
										_.isEmpty(record?.beneficiaryDeniedMessages) ||
										lastRelevantRecord?.message === null
									);
								},
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'wcAudits',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.wcAudits',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'statusChangeByBoUser.displayName',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.updatedBy',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedAt',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.updatedAt',
								required: true,
								disabled: true,
							},
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdAt',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.createdAt',
								required: true,
								disabled: true,
							},
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.BALANCE_REFUND]: [
		{
			name: 'page.customerAccounts.forms.balance_refund.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdAt',
								label: 'page.customerAccounts.forms.balance_refund.createdAt',
								required: true,
								disabled: true,
							},
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'settlementDate',
								label: 'page.customerAccounts.forms.balance_refund.completedAt',
								required: true,
								disabled: true,
							},
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : 'n/a',
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'statusHebrew',
								label: 'page.customerAccounts.forms.balance_refund.status',
								options: beneficiaryStatusOptions,
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'amount',
								label: 'page.customerAccounts.forms.balance_refund.amount',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'amount', value: null, type: fieldDependencyType.isEmpty },
							customValue: (value, record) =>
								_.has(record, 'symbol')
									? _.get(record, 'symbol') + formatNumber(value)
									: formatNumber(value),
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'currency.name',
								label: 'page.customerAccounts.forms.balance_refund.currency',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'reference',
								label: 'page.customerAccounts.forms.balance_refund.reference',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'ccId',
								label: 'ccId',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'reason',
								label: 'page.customerAccounts.forms.balance_refund.reason',
								required: false,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'note',
								label: 'page.customerAccounts.forms.balance_refund.note',
								required: false,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'payment.amount',
								label: 'page.customerAccounts.forms.balance_refund.payment.amount',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'payment.amount', value: null, type: fieldDependencyType.equals },
							customValue: (value, record) =>
								value
									? _.has(record, 'payment.currency.symbol')
										? _.get(record, 'payment.currency.symbol') + formatNumber(value)
										: formatNumber(value)
									: 'n/a',
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'payment.currency.symbol',
								label: 'page.customerAccounts.forms.balance_refund.symbol',
								required: false,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							dependency: {
								name: 'payment.currency.symbol',
								value: null,
								type: fieldDependencyType.equals,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'payment.currency.name',
								label: 'page.customerAccounts.forms.balance_refund.currency',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							dependency: {
								name: 'payment.currency.name',
								value: null,
								type: fieldDependencyType.equals,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'payment.createdAt',
								label: 'page.customerAccounts.forms.balance_refund.payment.createdAt',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'payment.createdAt', value: null, type: fieldDependencyType.equals },
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : 'n/a',
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'payment.updatedAt',
								label: 'page.customerAccounts.forms.balance_refund.payment.updatedAt',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							dependency: {
								name: 'payment.updatedAt',
								value: null,
								type: fieldDependencyType.equals,
							},
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : 'n/a',
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'payment.reference',
								label: 'page.customerAccounts.forms.balance_refund.reference',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							dependency: {
								name: 'payment.reference',
								value: null,
								type: fieldDependencyType.equals,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'payment.ccId',
								label: 'ccId',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							dependency: {
								name: 'payment.ccId',
								value: null,
								type: fieldDependencyType.equals,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdByBoUser.lastName',
								label: 'page.customerAccounts.forms.balance_refund.created_by_bo_user',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							dependency: {
								name: 'createdByBoUser.lastName',
								value: null,
								type: fieldDependencyType.equals,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdByBoUser.firstName',
								label: 'page.customerAccounts.forms.balance_refund.created_by_bo_user',
								required: true,
								disabled: true,
							},
							customValue: (value, record) =>
								value
									? _.has(record, 'createdByBoUser.lastName')
										? value + ' ' + _.get(record, 'createdByBoUser.lastName')
										: value
									: 'n/a',
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.SUPPORT_REQUESTS]: [
		{
			name: 'page.customerAccounts.forms.supportRequests.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'subject',
								label: 'page.customerAccounts.forms.supportRequests.subject',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'message',
								label: 'page.customerAccounts.forms.supportRequests.message',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'status',
								label: 'page.customerAccounts.forms.supportRequests.status',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'boNotes',
								label: 'page.customerAccounts.forms.supportRequests.boNotes',
								required: true,
								disabled: false,
							},
							dependency: { name: 'boNotes', value: !null },
							validation: [{ requierd: true }],
						},
					],
				},
			],
		},
	],
};

export const customerDashboardNonEditableTypesForms: formConfig = {
	[customerAccountDetailsTypes.BANK_ACCOUNTS]: [
		{
			name: 'page.customerAccounts.forms.bankAccounts.bankDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'accountOwnerName',
								label: 'page.customerAccounts.forms.bankAccounts.bankDetails.accountOwnerName',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'bankName',
								label: 'page.customerAccounts.forms.bankAccounts.bankDetails.bankName',
								required: false,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'bankCode',
								label: 'page.customerAccounts.forms.bankAccounts.bankDetails.bankCode',
								required: false,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'bankBranch',
								label: 'page.customerAccounts.forms.bankAccounts.bankDetails.bankBranch',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'accountNumber',
								label: 'page.customerAccounts.forms.bankAccounts.bankDetails.accountNumber',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.bankAccounts.bankAccountManagementDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'statusHebrew',
								label: 'page.customerAccounts.forms.bankAccounts.bankAccountManagementDetails.status',
								required: true,
								disabled: false,
								options: bankAccountStatusOptions,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'boNotes',
								label: 'page.customerAccounts.forms.bankAccounts.bankAccountManagementDetails.rejectReason',
								required: false,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'lastUpdatedBy',
								label: 'page.customerAccounts.forms.bankAccounts.bankAccountManagementDetails.lastUpdatedBy',
								required: true,
								disabled: true,
							},
							//validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedAt',
								label: 'page.customerAccounts.forms.bankAccounts.bankAccountManagementDetails.updatedAt',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
							customValue: (value) => formatDate(value),
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.bankAccounts.file.title',
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.file,
							mode: fieldMode.readonly,
							props: {
								name: 'file',
								label: 'page.customerAccounts.forms.bankAccounts.file.reference',
								required: true,
								disabled: true,
								onClick: (file) => {
									getFile(file?.key, file?.companyId);
								},
							},
							validation: [{ requierd: true }],
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.CONTACTS]: [
		{
			name: 'page.customerAccounts.forms.contacts.basicDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'firstNameHebrew',
								label: 'page.customerAccounts.forms.contacts.basicDetails.firstNamHebrew',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'lastNameHebrew',
								label: 'page.customerAccounts.forms.contacts.basicDetails.lastNameHebrew',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'firstName',
								label: 'page.customerAccounts.forms.contacts.basicDetails.firstNameEnglish',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'lastName',
								label: 'page.customerAccounts.forms.contacts.basicDetails.lastNameEnglish',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.datePicker,
							mode: fieldMode.readonly,
							props: {
								name: 'dateOfBirth',
								label: 'page.customerAccounts.forms.contacts.basicDetails.dateOfBirth',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'legalStatus',
								label: 'label.publicFigure',
								options: [
									{ value: ContactLegalStatus.Legal, label: 'label.yes', disabled: false },
									{ value: ContactLegalStatus.NotLegal, label: 'label.no', disabled: false },
								],
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
							customValue: (value) => (value === ContactLegalStatus.Legal ? 'כן' : 'לא'),
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'identificationTypeHebrew',
								label: 'page.customerAccounts.forms.contacts.basicDetails.identificationType',
								required: false,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'identificationNumber',
								label: 'page.customerAccounts.forms.contacts.basicDetails.identificationNumber',
								required: false,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						/*
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'genderHebrew',
								label: 'page.customerAccounts.forms.contacts.basicDetails.gender',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						*/
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.contacts.contactDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'email',
								label: 'page.customerAccounts.forms.contacts.contactDetails.email',
								required: false,
								disabled: false,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: {
								name: 'phone',
								label: 'page.customerAccounts.forms.contacts.contactDetails.phone',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }, { phone: true }],
						},
						/*
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'address',
								label: 'page.customerAccounts.forms.contacts.contactDetails.address',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'city',
								label: 'page.customerAccounts.forms.contacts.contactDetails.city',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'country.name',
								label: 'page.customerAccounts.forms.contacts.contactDetails.country',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						*/
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.contacts.accountManagementDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'role',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.role',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'permission',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.permission',
								options: permissionOptions,
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'maxForPayment',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.maxForPayment',
								required: true,
								disabled: true,
							},
							suffix: '$',
							validation: [{ requierd: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'maxForConvert',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.maxForConvert',
								required: true,
								disabled: true,
							},
							suffix: '$',
							validation: [{ requierd: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'statusHebrew',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.status',
								required: true,
								disabled: false,
								options: contactStatusOptions,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.textArea,
							mode: fieldMode.readonly,
							props: {
								name: 'boNotes',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.rejectReason',
								required: false,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedBy',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.updatedBy',
								required: false,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedAt',
								label: 'page.customerAccounts.forms.contacts.accountManagementDetails.updatedAt',
								required: false,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) => formatDate(value),
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.BENEFICIARIES]: [
		...initialBeneficiaryForms,
		{
			name: 'page.customerAccounts.forms.beneficiaries.accountDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'statusHebrew',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.status',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.textArea,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiaryDeniedMessages',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.rejectReason',
								required: false,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value, record) => {
								return getDeclinedReasonText(record);
							},
							dependency: {
								name: 'beneficiaryDeniedMessages',
								value: null,
								type: fieldDependencyType.isCustomDependency,
								custom: (record) => {
									const lastRelevantRecord = _.findLast(
										record?.beneficiaryDeniedMessages,
										'createdByContactId'
									);
									return (
										_.isEmpty(record) ||
										_.isEmpty(record?.beneficiaryDeniedMessages) ||
										lastRelevantRecord?.message === null
									);
								},
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'wcAudits',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.wcAudits',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'statusChangeByBoUser.displayName',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.updatedBy',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedAt',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.updatedAt',
								required: true,
								disabled: true,
							},
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdAt',
								label: 'page.customerAccounts.forms.beneficiaries.accountDetails.createdAt',
								required: true,
								disabled: true,
							},
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.BALANCE]: initialBalanceAccountForms,
	[customerAccountDetailsTypes.BALANCE_WITHDRAWAL]: initialWithdrawalBalanceAccountForms,
	[customerAccountDetailsTypes.BASIC_DATA]: [
		{
			name: 'page.customerAccounts.forms.basicData.companyDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'hebrewName',
								label: 'page.customerAccounts.forms.basicData.companyDetails.hebrewName',
								required: true,
								disabled: false,
							},
							//validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'englishName',
								label: 'page.customerAccounts.forms.basicData.companyDetails.englishName',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'customerType',
								label: 'page.customerAccounts.forms.basicData.companyDetails.customerType',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'companyIdentityNumber',
								label: 'page.customerAccounts.forms.basicData.companyDetails.companyIdentityNumber',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'addressHebrew',
								label: 'page.customerAccounts.forms.basicData.companyDetails.addressHebrew',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'address',
								label: 'page.customerAccounts.forms.basicData.companyDetails.address',
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.basicData.accountDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'accountId',
								label: 'page.customerAccounts.forms.basicData.accountDetails.accountId',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'ccId',
								label: 'page.customerAccounts.forms.basicData.accountDetails.ccId',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'hyperAccountNumber',
								label: 'page.customerAccounts.forms.basicData.accountDetails.hyperAccountNumber',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'maxForPayment',
								label: 'page.customerAccounts.forms.basicData.accountDetails.maxForPayment',
								required: false,
								disabled: false,
							},
							suffix: '$',
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'maxForConvert',
								label: 'page.customerAccounts.forms.basicData.accountDetails.maxForConvert',
								required: false,
								disabled: false,
							},
							suffix: '$',
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'riskLevel',
								label: 'page.customerAccounts.forms.basicData.accountDetails.riskLevel',
								options: riskTypeOptions,
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.readonly,
							props: {
								name: 'closedByBoUser',
								label: 'page.customerAccounts.forms.basicData.accountDetails.status',
								required: false,
								disabled: false,
							},
							customValue: (value) => (value === null ? accountStatus.ACTIVE : accountStatus.NOT_ACTIVE),
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.basicData.accountManagementDetails.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'manager.name',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.manager',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'dealer.name',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.dealer',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'agent.name',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.agent',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'lead.name',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.lead',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedBy',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.updatedBy',
								required: false,
								disabled: false,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedAt',
								label: 'page.customerAccounts.forms.basicData.accountManagementDetails.updatedAt',
								required: false,
								disabled: false,
							},
							customValue: (value) => formatDate(value),
						},
					],
				},
			],
		},
	],
	[customerAccountDetailsTypes.PAYMENT]: [
		{
			name: 'page.customerAccounts.forms.payment.details.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdAt',
								label: 'page.customerAccounts.forms.payment.details.createdAt',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'signerApprovalDate',
								label: 'page.customerAccounts.forms.payment.details.signedAt',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'boApprovalDate',
								label: 'page.customerAccounts.forms.payment.details.boApprovalDate',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'settlementDate',
								label: 'page.customerAccounts.forms.payment.details.settlementDate',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							validation: [{ requierd: false }],
							dependency: { name: 'settlementDate', value: null, type: fieldDependencyType.isEmpty },
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'reference',
								label: 'page.customerAccounts.forms.payment.details.reference',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'amount',
								label: 'page.customerAccounts.forms.payment.details.amount',
								required: true,
								disabled: true,
							},
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'currency.name',
								label: 'page.customerAccounts.forms.payment.details.currency',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'typeOfPaymentHebrew',
								label: 'page.customerAccounts.forms.payment.details.typeOfPayment',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'swiftFeeTypeHebrew',
								label: 'page.customerAccounts.forms.payment.details.feeType',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'swiftFeeTypeHebrew', value: null, type: fieldDependencyType.isEmpty },
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bicSwift',
								label: 'page.customerAccounts.forms.payment.details.swiftNumber',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'fee.amount',
								label: 'page.customerAccounts.forms.payment.details.fee',
								required: true,
								disabled: true,
							},
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'reasonTitleHebrew',
								label: 'page.customerAccounts.forms.payment.details.reasonTitle',
								required: true,
								disabled: true,
							},
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'reasonTitleHebrew', value: null, type: fieldDependencyType.isEmpty },
						},
						{
							type: fieldType.textArea,
							mode: fieldMode.readonly,
							props: {
								name: 'paymentComment',
								label: 'page.customerAccounts.forms.payment.details.paymentComment',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'paymentComment', value: null, type: fieldDependencyType.isEmpty },
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'complianceReference',
								label: 'page.customerAccounts.forms.payment.details.complianceReference',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'complianceReference', value: null, type: fieldDependencyType.isEmpty },
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'invoiceNumber',
								label: 'page.customerAccounts.forms.payment.details.invoiceNumber',
								required: true,
								disabled: true,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'createdBy',
								label: 'page.customerAccounts.forms.payment.details.createdBy',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'signedBy',
								label: 'page.customerAccounts.forms.payment.details.signedBy',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'approverName',
								label: 'page.customerAccounts.forms.payment.details.approvedBy',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'approverName', value: '', type: fieldDependencyType.equals },
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.payment.beneficiary.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.companyName',
								label: 'page.customerAccounts.forms.payment.beneficiary.companyName',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.nickname',
								label: 'page.customerAccounts.forms.payment.beneficiary.nickname',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.country.name',
								label: 'page.customerAccounts.forms.payment.beneficiary.country',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bankAccountHolderName',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.bankAccountHolderName',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bankCode',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.bankCode',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.branchCode',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.branchCode',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bankAccountAddress',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.bankAccountAddress',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bankAccountCity',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.bankAccountCity',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.bankAccountCountry.name',
								label: 'page.customerAccounts.forms.payment.beneficiaryBank.bankAccountCountry',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},

						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiary.currency.name',
								label: 'page.customerAccounts.forms.payment.beneficiary.currency',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'beneficiaryStatusHebrew',
								label: 'page.customerAccounts.forms.payment.beneficiary.status',
								required: false,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.payment.reference.title',
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						...createMultipleFileFields(5, true, 'page.customerAccounts.forms.payment.reference.file'),
					],
				},
			],
		},
		{
			name: 'page.customerAccounts.forms.payment.accountManagementDetails.status.title',
			theme: formThemes.hiddenInputs,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'ccId',
								label: 'page.customerAccounts.forms.payment.accountManagementDetails.ccId',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'ccId', value: null, type: fieldDependencyType.equals },
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'boApprovedByUser.firstName',
								label: 'page.customerAccounts.forms.payment.accountManagementDetails.boApprovedByUser',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependencyAction: dependencyAction.hide,
							dependency: {
								name: 'boApprovedByUser.firstName',
								value: null,
								type: fieldDependencyType.equals,
							},
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'updatedAt',
								label: 'page.customerAccounts.forms.payment.accountManagementDetails.updatedAt',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							customValue: (value) =>
								isValid(new Date(value)) ? format(new Date(value), timing.FULLDATE) : value,
						},
						{
							type: fieldType.text,
							mode: fieldMode.readonly,
							props: {
								name: 'statusHebrew',
								label: 'page.customerAccounts.forms.payment.status.status',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
						},
						{
							type: fieldType.textArea,
							mode: fieldMode.readonly,
							props: {
								name: 'rejectReason',
								label: 'page.customerAccounts.forms.payment.status.rejectReason',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'rejectReason', value: null, type: fieldDependencyType.equals },
						},
						{
							type: fieldType.textArea,
							mode: fieldMode.readonly,
							props: {
								name: 'boNotes',
								label: 'page.customerAccounts.forms.payment.status.rejectReason',
								required: true,
								disabled: true,
							},
							validation: [{ requierd: false }],
							dependencyAction: dependencyAction.hide,
							dependency: { name: 'boNotes', value: null, type: fieldDependencyType.isEmpty },
						},
					],
				},
			],
		},
	],
};

export const withdrawalBalanceTypesForms = () => {
	return {
		[withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST]: {
			name: 'page.withdrawal.title',
			theme: formThemes.regular,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.write,
							props: {
								name: 'contact_id',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST
								)}.contact_id`,
								required: true,
								disabled: false,
								options: getCompanyContactsOptions(),
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.write,
							props: {
								name: 'currency_id',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST
								)}.currency_id`,
								required: true,
								disabled: false,
								options: getCompanyWithdrawalCurrenciesOptions(),
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.write,
							props: {
								name: 'amount',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST
								)}.amount`,
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true, balance: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.textArea,
							mode: fieldMode.write,
							props: {
								name: 'reason',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST
								)}.reason`,
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.radio,
							mode: fieldMode.write,
							props: {
								name: 'close_account',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST
								)}.close_account`,
								required: true,
								disabled: false,
								options: [
									{
										value: 'true',
										label: `page.withdrawal.forms.${_.snakeCase(
											withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST
										)}.close_account.yes`,
										disabled: false,
									},
									{
										value: 'false',
										label: `page.withdrawal.forms.${_.snakeCase(
											withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST
										)}.close_account.no`,
										disabled: false,
									},
								],
							},
							validation: [{ requierd: true }],
						},
					],
				},
			],
		},

		[withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_CC_TO_BANK_ACCOUNT]: {
			name: 'page.withdrawal.title',
			theme: formThemes.regular,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.write,
							props: {
								name: 'house_account_transfer_currency_id',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_CC_TO_BANK_ACCOUNT
								)}.currency_id`,
								required: true,
								disabled: false,
								options: getCompanyWithdrawalCurrenciesOptions(),
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.write,
							props: {
								name: 'house_account_transfer_amount',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_CC_TO_BANK_ACCOUNT
								)}.amount`,
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true, balance: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.select,
							mode: fieldMode.write,
							props: {
								name: 'altshuler_account_id',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_CC_TO_BANK_ACCOUNT
								)}.altshuler_account_id`,
								required: true,
								disabled: false,
								options: getAltshulerAccountsOptions(),
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.write,
							props: {
								name: 'cc_reference',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_CC_TO_BANK_ACCOUNT
								)}.cc_reference`,
								required: true,
								disabled: true,
							},
							validation: [{ requierd: true }],
						},
					],
				},
			],
		},

		[withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_TO_CUSTOMER_BANK_ACCOUNT]: {
			name: 'page.withdrawal.title',
			theme: formThemes.regular,
			handleChange: () => { },
			fieldsGroups: [
				{
					type: fieldsGroupType.col,
					fields: [
						{
							type: fieldType.select,
							mode: fieldMode.write,
							props: {
								name: 'final_altshuler_transfer_account_id',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_TO_CUSTOMER_BANK_ACCOUNT
								)}.altshuler_account_id`,
								required: true,
								disabled: false,
								options: getAltshulerAccountsOptions(),
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.select,
							mode: fieldMode.write,
							props: {
								name: 'final_altshuler_transfer_currency_id',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_TO_CUSTOMER_BANK_ACCOUNT
								)}.currency_id`,
								required: true,
								disabled: false,
								options: getCompanyWithdrawalCurrenciesOptions(),
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.write,
							props: {
								name: 'final_altshuler_account_transfer_amount',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_TO_CUSTOMER_BANK_ACCOUNT
								)}.amount`,
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true, balance: true }],
							customValueType: customValueTypes.integer,
						},
						{
							type: fieldType.select,
							mode: fieldMode.write,
							props: {
								name: 'customer_account_id',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_TO_CUSTOMER_BANK_ACCOUNT
								)}.customer_account_id`,
								required: true,
								disabled: false,
								options: getCompanyBankAccountsOptions(),
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.text,
							mode: fieldMode.write,
							props: {
								name: 'reference',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_TO_CUSTOMER_BANK_ACCOUNT
								)}.reference`,
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
						{
							type: fieldType.file,
							mode: fieldMode.write,
							props: {
								name: 'transfer_file',
								label: `page.withdrawal.forms.${_.snakeCase(
									withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_TO_CUSTOMER_BANK_ACCOUNT
								)}.transfer_file`,
								required: true,
								disabled: false,
							},
							validation: [{ requierd: true }],
						},
					],
				},
			],
		},
	};
};
